import { SubstitutionType, TemplateRuleRow } from "./RulesModal.types";
import { makeCygnusApiCall } from "../../../../../api/api";

/**
 * Validates a rule form.
 */
export const validateRuleForm = (
  ruleName: string,
  selectedField: { label: string; value: string } | null,
  operator: { label: string; value: string } | null,
  conditionValue: string,
  defaultValue: { label: string; value: string } | null,
  substitutionValue: string,
  existingRows: TemplateRuleRow[],
  editingRow: TemplateRuleRow | null,
  selectedFieldsList: { label: string; value: string }[],
  currentRuleName: string | undefined,
  substitutionType: "TEXT" | "IMAGE",
  imageFile?: File | null
): { valid: boolean; errors: Record<string, string> } => {
  
  const errors: Record<string, string> = {};

  if (substitutionType === "IMAGE" && !imageFile && !editingRow) {
    errors.substitutionImage = "Please upload an image";
  }

  const emptyMsg = "Field cannot be empty";

  if (!ruleName?.trim()) errors.ruleName = emptyMsg;
  if (!selectedField?.label.trim()) errors.selectedField = emptyMsg;
  if (!operator?.label.trim()) errors.operator = emptyMsg;
  if (!conditionValue?.trim()) errors.conditionValue = emptyMsg;
  if (!defaultValue?.label.trim()) errors.defaultValue = emptyMsg;
  if (
    substitutionType === "TEXT" &&
    !substitutionValue?.trim()
  ) {
    errors.substitutionValue = emptyMsg;
  }
  
  if (ruleName) {
    const lowerCaseRuleName = ruleName.toLowerCase();

    const isSelectedFieldNameMatch = selectedFieldsList
      .map(f => f.label.toLowerCase())
      .includes(lowerCaseRuleName);

    const isDuplicateCondition = existingRows
      .filter(row => row !== editingRow)
      .some(row =>
        row.SelectedFieldValue === selectedField?.value &&
        row.Operator === operator?.value &&
        row.ConditionValue.toLowerCase() === conditionValue.toLowerCase()
      );

    if (isSelectedFieldNameMatch) {
      errors.ruleName = "The rule name must be distinct from the selected field names.";
    }
    if (isDuplicateCondition) {
      errors.conditionValue = "The condition value already exists.";
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Creates a row object for saving.
 */
export const buildNewRow = (
  ruleName: string,
  selectedField: { label: string; value: string } | null,
  operator: { label: string; value: string } | null,
  conditionValue: string,
  defaultValue: { label: string; value: string } | null,
  substitutionValue: string,
  substitutionType: SubstitutionType,
  substitutionImage: string | undefined,
  editingRow: TemplateRuleRow | null,
  templateRuleId?: number
): TemplateRuleRow => ({
  TemplateRuleId: editingRow?.TemplateRuleId ?? templateRuleId ?? 0,
  TemplateRuleDetailId: editingRow?.TemplateRuleDetailId || 0,
  RuleName: ruleName || "",
  SelectedFieldLabel: selectedField?.label || "",
  SelectedFieldValue: selectedField?.value || "",
  Operator: operator?.label || "",
  ConditionValue: conditionValue,
  DefaultValue: defaultValue?.label || "",
  SubstitutionType: substitutionType,
  SubstitutionValue: substitutionValue,
  SubstitutionImage: substitutionImage,
  Active: "Y",
  RuleActive: "Y",
});


/**
 * Fetches template rule details.
 * Supports:
 *  - All rules
 *  - Rule by TemplateRuleId
 *  - Single rule condition by TemplateRuleDetailId
 */
export const fetchTemplateFieldRulesDetails = async (
  params?: {
    TemplateRuleId?: number;
    TemplateRuleDetailId?: number;
    SubstitutionType?: string;
  }
): Promise<any[]> => {
  const query = new URLSearchParams();

  if (params?.TemplateRuleId) {
    query.append("TemplateRuleId", String(params.TemplateRuleId));
  }

  if (params?.TemplateRuleDetailId) {
    query.append("TemplateRuleDetailId", String(params.TemplateRuleDetailId));
  }

  if (params?.SubstitutionType) {
    query.append("SubstitutionType", String(params.SubstitutionType));
  }

  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/template_rule_details/?${query.toString()}`,
    show_message: false,
    customized_success_message: "Fetched rule details.",
    customized_failure_message: "Failed to fetch rule details.",
    customized_error_message: "Error occurred while fetching rule details.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};